import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useGetApprovalLimitsQuery, useGetApprovalLimitByIdQuery, useCreateApprovalLimitMutation, useUpdateApprovalLimitMutation } from '../../../../../api/endpoints/approvalLimits';
import { useGetApprovalLimitGroupsQuery } from '../../../../../api/endpoints/approvalLimitGroups';
import { useGetUserGroupsQuery } from '../../../../../api/endpoints/user';
import { FilterMatchMode } from 'primereact/api';
import { createInitialFilters, convertFiltersToPrimeReactFormat } from '../../../../../utils/filterUtils';
import { useLanguage } from '../../../../../contexts/LanguageContext';

const useApprovalLimits = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [historyDialogVisible, setHistoryDialogVisible] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentItemId, setCurrentItemId] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [historySelectedItem, setHistorySelectedItem] = useState(null);
    const [editingItemId, setEditingItemId] = useState(null);

    const [lazyParams, setLazyParams] = useState({ page: 1, rows: 10 });
    const [apiFilters, setApiFilters] = useState({});
    const [filters, setFilters] = useState({});

    // API calls for dropdown options
    const { data: approvalGroupsData } = useGetApprovalLimitGroupsQuery({
        IsActive: 1,
        PageSize: 100
    });

    const { data: userGroupsData } = useGetUserGroupsQuery();

    // Prepare dropdown options
    const customFilterOptions = useMemo(() => {
        const approvalGroupOptions =
            approvalGroupsData?.items?.map((group) => ({
                label: group.groupName,
                value: group.id
            })) || [];

        const currencyOptions = [
            { label: 'TRY', value: 'TRY' },
            { label: 'USD', value: 'USD' },
            { label: 'EUR', value: 'EUR' }
        ];

        return {
            approvalGroupId: approvalGroupOptions,
            currency: currencyOptions
        };
    }, [approvalGroupsData]);

    const filterFields = useMemo(
        () => [
            { field: 'currency', headerName: th('labor.currency'), type: 'select', options: customFilterOptions.currency || [] },
            { field: 'approvalGroupId', headerName: th('approvalGroups.title'), type: 'select', options: customFilterOptions.approvalGroupId || [] },
            { field: 'startDate', headerName: th('common.startDate'), type: 'date' },
            { field: 'finishDate', headerName: th('common.endDate'), type: 'date' }
        ],
        [th, customFilterOptions]
    );

    const filterTypeMap = useMemo(
        () => ({
            currency: { type: 'select', matchMode: FilterMatchMode.EQUALS },
            approvalGroupId: { type: 'select', matchMode: FilterMatchMode.EQUALS },
            startDate: { type: 'date', matchMode: FilterMatchMode.EQUALS },
            finishDate: { type: 'date', matchMode: FilterMatchMode.EQUALS }
        }),
        []
    );

    const filterFieldNames = useMemo(() => filterFields.map((f) => f.field), [filterFields]);
    const initialFilters = useMemo(() => createInitialFilters(filterFieldNames, filterTypeMap), [filterFieldNames, filterTypeMap]);

    useEffect(() => setFilters(initialFilters), [initialFilters]);

    const {
        data: approvalLimitsData,
        isLoading,
        refetch
    } = useGetApprovalLimitsQuery({
        pageNumber: lazyParams.page,
        pageSize: lazyParams.rows,
        isActive: 1,
        ...apiFilters
    });

    // API query for getting approval limit by ID (for editing)
    const {
        data: approvalLimitByIdData,
        isLoading: isLoadingById,
        error: errorById
    } = useGetApprovalLimitByIdQuery(editingItemId, {
        skip: !editingItemId
    });

    const [createItem] = useCreateApprovalLimitMutation();
    const [updateItem] = useUpdateApprovalLimitMutation();

    useEffect(() => {
        if (approvalLimitByIdData && editingItemId) {
            // Do not open dialog yet, wait one render cycle to ensure selectedItem is set
            setSelectedItem(approvalLimitByIdData);
            setCurrentItemId(editingItemId);
            setEditMode(true);
            setSubmitted(false);

            // Delay setting dialogVisible to ensure form catches updated selectedItem
            setTimeout(() => {
                setDialogVisible(true);
            }, 0);

            setEditingItemId(null); // Reset to prevent multiple calls
        }
    }, [approvalLimitByIdData, editingItemId]);

    // Handle API error for editing
    useEffect(() => {
        if (errorById && editingItemId) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('messages.loadError'),
                life: 3000
            });
            setEditingItemId(null);
        }
    }, [errorById, editingItemId, th]);

    const resetForm = () => {
        setSelectedItem(null);
        setCurrentItemId(null);
        setEditMode(false);
        setSubmitted(false);
        setEditingItemId(null);
    };

    const openNewDialog = () => {
        resetForm();
        setDialogVisible(true);
    };

    const onSubmit = async (formData) => {
        setSubmitted(true);
        const { startDate, finishDate, startingPrice, endingPrice, currency, approvalGroupId } = formData;

        if (!startDate || startingPrice === null || endingPrice === null || !currency || !approvalGroupId) {
            console.log('❌ Validation failed:', formData);
            return;
        }

        const payload = {
            startDate: startDate instanceof Date ? startDate.toISOString() : startDate,
            finishDate: finishDate ? (finishDate instanceof Date ? finishDate.toISOString() : finishDate) : null,
            startingPrice: Number(startingPrice),
            endingPrice: Number(endingPrice),
            currency: currency,
            approvalGroupId: Number(approvalGroupId)
        };

        try {
            if (editMode && currentItemId) {
                const updatePayload = {
                    id: currentItemId,
                    ...payload,
                    isActive: 1
                };
                await updateItem(updatePayload).unwrap();
                toast.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('messages.updateSuccess'),
                    life: 3000
                });
            } else {
                await createItem(payload).unwrap();
                toast.current?.show({
                    severity: 'success',
                    summary: th('common.success'),
                    detail: th('messages.saveSuccess'),
                    life: 3000
                });
            }
            setDialogVisible(false);
            resetForm();
            refetch();
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('messages.saveError'),
                life: 3000
            });
        }
    };

    const handleEdit = (item) => {
        // Reset any previous state
        resetForm();
        // Set the editing item ID to trigger the API call
        setEditingItemId(item.id);
    };

    const handleHistory = (item) => {
        setHistorySelectedItem(item);
        setHistoryDialogVisible(true);
    };

    const handlePage = useCallback((event) => {
        setLazyParams({
            page: event.page + 1,
            rows: event.rows
        });
    }, []);

    const handleApplyFilters = useCallback(
        (updatedFilters) => {
            const converted = convertFiltersToPrimeReactFormat(updatedFilters, filters, filterTypeMap);
            setFilters(converted);

            const newApiFilters = {};
            Object.entries(updatedFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== '' && value !== null) {
                    let processedValue = value;

                    // Process date fields
                    if (key === 'startDate' || key === 'finishDate') {
                        processedValue = value instanceof Date ? value.toISOString() : value;
                    }

                    // Process all filter fields
                    if (key === 'currency' || key === 'approvalGroupId' || key === 'startDate' || key === 'finishDate') {
                        newApiFilters[key] = processedValue;
                    }
                }
            });
            setApiFilters(newApiFilters);
            setLazyParams((prev) => ({ ...prev, page: 1 })); // Reset to first page when filtering
        },
        [filters, filterTypeMap]
    );

    const handleResetFilters = () => {
        setFilters(initialFilters);
        setApiFilters({});
        setLazyParams((prev) => ({ ...prev, page: 1 }));
    };

    return {
        toast,
        dialogVisible,
        setDialogVisible,
        historyDialogVisible,
        setHistoryDialogVisible,
        historySelectedItem,
        openNewDialog,
        resetForm,
        filterFields,
        customFilterTypes: {},
        customFilterOptions,
        handleSearch: () => {},
        handleApplyFilters,
        handleResetFilters,

        tableProps: {
            data: approvalLimitsData?.items || [],
            totalRecords: approvalLimitsData?.totalCount || 0,
            isLoading,
            onPage: handlePage,
            lazyParams: { first: (lazyParams.page - 1) * lazyParams.rows, rows: lazyParams.rows },
            onEdit: handleEdit,
            onHistory: handleHistory,
            resetPagination: false
        },
        formProps: {
            visible: dialogVisible,
            onSubmit,
            submitted,
            editMode,
            selectedItem
        }
    };
};

export default useApprovalLimits;
